// Sequencer Connect - the progress view.
//
// What the shell renders once a run has left the wizard: `running`, `qa`,
// `completed` or `failed`. It is a pure reader of `detail` plus two writes, the
// two retry endpoints; the shell owns the fetching and the polling.
//
// Everything painted here comes off `run.status` and the item rows, NEVER off
// `current_step` - the wizard pointer stops moving at Start and would freeze
// this screen on whatever step the operator happened to leave it at.
//
// Two things this view refuses to lie about:
//
//  1. `skipped_active`. A retry that answers `retried: 3, skipped_active: [a, b]`
//     did NOT re-drive a and b - their jobs were still live, so they were left
//     completely alone. Reporting the count alone would tell the operator five
//     mailboxes are moving when three are.
//  2. QA. A run whose credentials were purged mid-QA completes with items that
//     were never verified. Those cells read "-", not a tick, and a notice says
//     why - a green check nobody earned is the one thing this screen must not
//     print.
//
// SECURITY: nothing here can render a borrowed credential; `detail` carries only
// booleans and timestamps about the three secrets (see sequencerApi.ts).

import { useState } from 'react';

import {
  sqApi,
  sqErrorCode,
  sqErrorMessage,
  type SqItemStatus,
  type SqRetryResult,
} from '../sequencerApi';
import type { SqStepProps } from './shared';

/** The shell's props minus the wizard-only pieces. A run past Start has no step
 *  to move to, so `goToStep` is deliberately not in scope here. */
export type SqProgressProps = Pick<
  SqStepProps,
  'runId' | 'detail' | 'reload' | 'notifyEnqueued' | 'toast'
>;

/** Compact and unambiguous: a run can straddle midnight, so the day stays. */
function fmtTime(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
}

/** Failures first, then whatever is still moving, then the rest. Array.sort is
 *  stable, so inside each rank the paste order survives. */
function sortRank(s: SqItemStatus): number {
  if (s === 'error') return 0;
  if (s === 'connected' || s === 'running') return 1;
  return 2;
}

/** Name the mailboxes, but do not paste 200 addresses into a toast. */
function nameEmails(emails: string[]): string {
  const head = emails.slice(0, 3).join(', ');
  return emails.length > 3 ? `${head} and ${emails.length - 3} more` : head;
}

function Tile({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'blue' | 'muted';
  hint?: string;
}): JSX.Element {
  return (
    <div className="sq-tile">
      <span className="sq-tile-label">{label}</span>
      <span className={`sq-tile-value sq-tile-value--${variant}`}>{value}</span>
      {hint && <span className="sq-tile-hint">{hint}</span>}
    </div>
  );
}

export function ProgressView({
  runId,
  detail,
  reload,
  notifyEnqueued,
  toast,
}: SqProgressProps): JSX.Element {
  const { run, items } = detail;
  // Connect only: nothing is configured, so nothing here may say it was.
  const connectOnly = run.sequencer === 'warmuptool';

  // Which button is spinning: 'all' for the header retry, the email for a row.
  // One at a time - two retries against the same run race each other's resets.
  const [busy, setBusy] = useState<string | null>(null);

  const inflight = run.status === 'running' || run.status === 'qa';

  const total = items.length;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const alreadyCount = items.filter((i) => i.status === 'already').length;
  const errorCount = items.filter((i) => i.status === 'error').length;
  const qaCount = items.filter((i) => i.qa_verified_at !== null).length;

  /**
   * The server's own retry gate, mirrored (secretsGone in routes/sequencer-runs:
   * the key plus the computed has_item_passwords, on every platform).
   *
   * Not defensive polish: a run that completes CLEAN purges its credentials, so
   * every button on a green run would otherwise fail with SECRETS_PURGED on the
   * first click. The buttons still render - the operator has to see that rerun
   * exists - but they say why they cannot fire instead of discovering it.
   */
  const credsGone =
    run.secrets_purged_at !== null ||
    !run.has_api_key ||
    !run.has_item_passwords;

  // Why a retry cannot be clicked right now, or null when it can.
  const retryBlocked: string | null = run.archived_at
    ? 'This run is archived. Create a new run.'
    : run.status === 'qa'
      ? 'The QA pass re-drives failed accounts itself. Wait for it to finish.'
      : credsGone
        ? "This run's borrowed credentials were purged, so nothing on it can be rerun."
        : null;

  /**
   * Both retry buttons land here.
   *
   * `notifyEnqueued` opens the shell's pickup window so the poll runs even while
   * the re-queued jobs are still waiting for a worker - without it the screen
   * would sit still until the operator refreshed by hand.
   */
  async function retry(key: string, call: () => Promise<SqRetryResult>): Promise<void> {
    setBusy(key);
    try {
      const result = await call();
      if (result.enqueued > 0) notifyEnqueued();

      if (result.skipped_active.length > 0) {
        toast(
          'warn',
          `${result.retried} mailbox(es) re-queued. ${result.skipped_active.length} still had a job running and were left untouched: ${nameEmails(result.skipped_active)}. Retry those once it lands.`,
        );
      } else if (result.retried === 0) {
        toast('warn', 'Nothing was re-queued - no mailbox on this run was in a retryable state.');
      } else {
        toast('success', `${result.retried} mailbox(es) re-queued.`);
      }
      await reload();
    } catch (err) {
      const code = sqErrorCode(err);
      if (code === 'QA_IN_PROGRESS' || code === 'NOT_READY') {
        toast('warn', sqErrorMessage(err));
      } else if (code === 'SECRETS_PURGED') {
        // The router's own message tells the operator to re-enter the key and
        // re-paste the sheet, which is wizard advice: this run is past the
        // wizard and can never take those writes again.
        toast(
          'error',
          "This run's borrowed credentials were purged, so nothing on it can be rerun. Create a new run for the mailboxes that still need work.",
        );
      } else {
        toast('error', sqErrorMessage(err));
      }
      await reload();
    } finally {
      setBusy(null);
    }
  }

  // retry-errors on a `failed` run also re-drives the items stranded `pending`
  // by an enqueue catastrophe, so the button belongs on that status too - and
  // its count has to include them, or a 500-mailbox run whose queue add threw
  // offers "Retry failed (0)".
  const showRetryAll = run.accounts_errored > 0 || run.status === 'failed';
  const strandedCount =
    run.status === 'failed' ? items.filter((i) => i.status === 'pending').length : 0;
  const retryTargets = errorCount + strandedCount;

  // Completed, but some landed mailbox never got a QA read - the QA pass found
  // the credentials gone and completed the run unverified rather than parking it.
  const someUnverified =
    run.status === 'completed' &&
    items.some((i) => (i.status === 'done' || i.status === 'already') && i.qa_verified_at === null);

  const sorted = [...items].sort((a, b) => sortRank(a.status) - sortRank(b.status));

  return (
    <>
      <section className="sq-card">
        <div className="sq-card-header">
          <h2 className="sq-card-title">
            Progress
            {inflight && <span className="sq-pulse" />}
          </h2>
          <div className="sq-actions">
            {showRetryAll && (
              <button
                type="button"
                className="sq-btn sq-btn--primary sq-btn--small"
                onClick={() => void retry('all', () => sqApi.retryErrors(runId))}
                disabled={busy !== null || retryBlocked !== null}
                title={retryBlocked ?? 'Re-drive every mailbox that did not land'}
              >
                {busy === 'all' ? 'Retrying...' : `Retry failed (${retryTargets})`}
              </button>
            )}
            <button
              type="button"
              className="sq-btn sq-btn--secondary sq-btn--small"
              onClick={() => void reload()}
              disabled={busy !== null}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="sq-card-body">
          {run.status === 'running' && (
            <div className="sq-notice sq-notice--info sq-notice--live">
              <span className="sq-pulse" />
              Connecting accounts...
            </div>
          )}
          {run.status === 'qa' && (
            <div className="sq-notice sq-notice--warn">
              QA verification in progress - re-checking every account against the workspace and
              auto-retrying anything not spot on.
            </div>
          )}
          {/* `someUnverified` suppresses this one on purpose: a clean run whose
              QA pass found the credentials already gone completes with items
              nobody read back, and claiming they are QA-verified there would be
              the one lie this screen exists to avoid. The honey notice below
              takes its place. */}
          {run.status === 'completed' && run.accounts_errored === 0 && !someUnverified && (
            <div className="sq-notice sq-notice--ok">
              {connectOnly
                ? 'All accounts connected, each confirmed by its session outcome. Credentials purged.'
                : 'All accounts connected, configured and QA-verified. Credentials purged.'}
            </div>
          )}
          {run.status === 'completed' && run.accounts_errored > 0 && (
            <div className="sq-notice sq-notice--err">
              {run.accounts_errored === 1
                ? '1 account needs attention'
                : `${run.accounts_errored} accounts need attention`}{' '}
              - fix the root cause and rerun them individually.
            </div>
          )}
          {run.status === 'failed' && (
            <div className="sq-notice sq-notice--err">
              This run stopped before every mailbox was queued. Retry failed puts the ones that
              never started back on the queue.
            </div>
          )}

          {/* Archive works in every status, so an archived run reaches this
              screen. Its retry buttons are dead; say so once here rather than
              leaving the reason in a tooltip. */}
          {run.archived_at && (
            <div className="sq-notice sq-notice--warn">
              This run is archived and its credentials were purged. Nothing on it can be rerun -
              create a new run for anything that still needs work.
            </div>
          )}

          {/* Purged without being archived: the stale-secret sweep got to it.
              Only worth saying when there is something left to rerun - a clean
              run's sage banner already reports the purge. */}
          {!run.archived_at && credsGone && errorCount > 0 && (
            <div className="sq-notice sq-notice--warn">
              This run's borrowed credentials were purged, so nothing on it can be rerun. Create a
              new run for the mailboxes that still need work.
            </div>
          )}

          {someUnverified && (
            <div className="sq-notice sq-notice--warn">
              Some accounts were not QA-verified (credentials were purged) - create a new run for
              them if needed.
            </div>
          )}

          {/* Run-level catastrophe only. Per-mailbox failures live on the rows. */}
          {run.error_message && <div className="sq-notice sq-notice--err">{run.error_message}</div>}

          <div className="sq-tiles">
            <Tile label="Total" value={total} hint="mailboxes" />
            <Tile
              label={connectOnly ? 'Connected' : 'Connected & configured'}
              value={doneCount}
              variant={doneCount > 0 ? 'sage' : 'muted'}
            />
            <Tile
              label="Already in workspace"
              value={alreadyCount}
              variant={alreadyCount > 0 ? 'sage' : 'muted'}
              hint="not re-connected"
            />
            <Tile label="Failed" value={errorCount} variant={errorCount > 0 ? 'rose' : 'muted'} />
            <Tile
              label="QA verified"
              value={qaCount}
              variant={qaCount > 0 ? 'sage' : 'muted'}
              hint={`of ${doneCount + alreadyCount} landed`}
            />
          </div>
        </div>
      </section>

      <section className="sq-card">
        <div className="sq-card-header">
          <h2 className="sq-card-title">Accounts ({items.length})</h2>
        </div>

        {items.length === 0 ? (
          <div className="sq-empty">No mailboxes on this run.</div>
        ) : (
          <div className="sq-table-wrap">
            <table className="sq-table">
              <thead>
                <tr>
                  <th className="sq-col-num">#</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Connected</th>
                  {!connectOnly && <th>Configured</th>}
                  <th className="sq-col-center">QA</th>
                  <th>Error</th>
                  <th className="sq-col-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((it) => {
                  const bad = it.status === 'error';
                  // Rerunning a finished mailbox is a real move (a QA-verified
                  // account can still be wrong in the operator's eyes) once the
                  // run has stopped. While it is still running, every row is
                  // rerunnable too: a worker restart (every deploy restarts all
                  // 13) leaves items pinned non-terminal with no job behind them,
                  // and that wedges the run. Re-driving a mailbox whose job is
                  // genuinely live is not a risk here - the route's
                  // clearPriorJob refuses those and returns them as
                  // skipped_active.
                  const canRerun =
                    bad ||
                    run.status === 'running' ||
                    (run.status === 'completed' && (it.status === 'done' || it.status === 'already'));
                  return (
                    <tr key={it.email} className={bad ? 'sq-row--bad' : undefined}>
                      <td className="sq-col-num sq-cell-faint">{it.position + 1}</td>
                      <td className="sq-cell-mono">{it.email}</td>
                      <td>
                        <span className={`sq-badge sq-badge--${it.status}`}>{it.status}</span>
                      </td>
                      <td className="sq-cell-faint">{fmtTime(it.connected_at)}</td>
                      {!connectOnly && <td className="sq-cell-faint">{fmtTime(it.configured_at)}</td>}
                      <td className="sq-col-center">
                        {it.qa_verified_at ? (
                          <span className="sq-tick" title={fmtTime(it.qa_verified_at)}>
                            ✓
                          </span>
                        ) : (
                          <span className="sq-cell-faint">-</span>
                        )}
                      </td>
                      <td className="sq-cell-error">
                        {it.error_code || it.error_message ? (
                          // Open on a red row: the QA diff ("daily_limit:
                          // expected 15, live 10") IS the fix, and hiding it
                          // behind a click costs the operator the whole point.
                          <details className="sq-errors" open={bad}>
                            <summary>
                              <span className="sq-code">{it.error_code ?? 'ERROR'}</span>
                            </summary>
                            <div className="sq-errmsg">
                              {it.error_message ?? 'No message was recorded.'}
                            </div>
                          </details>
                        ) : (
                          <span className="sq-cell-faint">-</span>
                        )}
                      </td>
                      <td className="sq-col-center">
                        {canRerun ? (
                          <button
                            type="button"
                            className="sq-btn sq-btn--ghost sq-btn--small"
                            onClick={() =>
                              void retry(it.email, () => sqApi.retryItem(runId, it.email))
                            }
                            disabled={busy !== null || retryBlocked !== null}
                            title={retryBlocked ?? `Re-drive ${it.email}`}
                          >
                            {busy === it.email ? 'Rerunning...' : 'Rerun'}
                          </button>
                        ) : (
                          <span className="sq-cell-faint">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
